/**
 * Adapted from PlayCanvas Engine's framework/parsers/ply.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import { GSplatCompressedData } from '@/plugins/gsplat/core/GSplatCompressedData'
import {
  GSplatData,
  type PlyElement,
  type PlyProperty,
  type PlyStorage,
} from '@/plugins/gsplat/core/GSplatData'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type {
  GSplatHandler,
  GSplatLoadUrlObject,
  GSplatParserContext,
  IGSplatParser,
} from '@/plugins/gsplat/loaders/GSplatHandler'
import { StreamBuf } from '@/plugins/gsplat/loaders/StreamBuf'
import { GSplatCompressedResource } from '@/plugins/gsplat/unified/resource/GSplatCompressedResource'
import { GSplatResource } from '@/plugins/gsplat/unified/resource/GSplatResource'

const MAGIC_BYTES = new Uint8Array([112, 108, 121, 10])
const END_HEADER_BYTES = new Uint8Array([10, 101, 110, 100, 95, 104, 101, 97, 100, 101, 114, 10])

type PlyStorageConstructor =
  | Int8ArrayConstructor
  | Uint8ArrayConstructor
  | Int16ArrayConstructor
  | Uint16ArrayConstructor
  | Int32ArrayConstructor
  | Uint32ArrayConstructor
  | Float32ArrayConstructor
  | Float64ArrayConstructor

const DATA_TYPE_MAP = new Map<string, PlyStorageConstructor>([
  ['char', Int8Array],
  ['uchar', Uint8Array],
  ['short', Int16Array],
  ['ushort', Uint16Array],
  ['int', Int32Array],
  ['uint', Uint32Array],
  ['float', Float32Array],
  ['double', Float64Array],
])

export type PlyData = GSplatData | GSplatCompressedData
export type PlyResource =
  | GSplatResource<GSplatData>
  | GSplatCompressedResource<GSplatCompressedData>

export interface PlyParserOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly propertyFilter?: (name: string) => boolean
  readonly progress?: (loadedBytes: number, totalBytes: number) => void
  readonly onData?: (data: PlyData) => void
  readonly reorder?: boolean
  readonly decompress?: boolean
  readonly prepareCenters?: boolean
  /** header 给出可信数量后、分配完整 CPU/GPU 数据前执行。 */
  readonly validateSplatCount?: (count: number) => void
}

interface ParsedHeader {
  readonly elements: PlyElement[]
  readonly format: string | undefined
  readonly comments: string[]
}

function parseHeader(lines: readonly string[]): ParsedHeader {
  const elements: PlyElement[] = []
  const comments: string[] = []
  let format: string | undefined
  for (let index = 1; index < lines.length; index++) {
    const words = lines[index].split(' ')
    switch (words[0]) {
      case 'comment':
        comments.push(words.slice(1).join(' '))
        break
      case 'format':
        format = words[1]
        break
      case 'element': {
        const count = Number.parseInt(words[2], 10)
        if (!words[1] || !Number.isSafeInteger(count) || count < 0) {
          throw new Error(`Invalid element declaration '${lines[index]}' in ply header`)
        }
        elements.push({ name: words[1], count, properties: [] })
        break
      }
      case 'property': {
        const storageType = DATA_TYPE_MAP.get(words[1])
        if (!storageType) {
          throw new Error(`Unrecognized property data type '${words[1]}' in ply header`)
        }
        const element = elements.at(-1)
        if (!element || !words[2]) throw new Error('Property defined before element in ply header')
        element.properties.push({
          type: words[1],
          name: words[2],
          storage: null,
          byteSize: storageType.BYTES_PER_ELEMENT,
        })
        break
      }
      default:
        throw new Error(`Unrecognized header value '${words[0]}' in ply header`)
    }
  }
  return { elements, format, comments }
}

function isCompressedPly(elements: readonly PlyElement[]): boolean {
  const chunkProperties = [
    'min_x',
    'min_y',
    'min_z',
    'max_x',
    'max_y',
    'max_z',
    'min_scale_x',
    'min_scale_y',
    'min_scale_z',
    'max_scale_x',
    'max_scale_y',
    'max_scale_z',
    'min_r',
    'min_g',
    'min_b',
    'max_r',
    'max_g',
    'max_b',
  ]
  const vertexProperties = ['packed_position', 'packed_rotation', 'packed_scale', 'packed_color']
  const shProperties = Array.from({ length: 45 }, (_, index) => `f_rest_${index}`)
  const hasBaseElements = () =>
    elements[0]?.name === 'chunk' &&
    elements[0].properties.every(
      (property, index) => property.name === chunkProperties[index] && property.type === 'float'
    ) &&
    elements[1]?.name === 'vertex' &&
    elements[1].properties.every(
      (property, index) => property.name === vertexProperties[index] && property.type === 'uint'
    )
  const hasSHElements = () =>
    elements[2]?.name === 'sh' &&
    [9, 24, 45].includes(elements[2].properties.length) &&
    elements[2].properties.every(
      (property, index) => property.name === shProperties[index] && property.type === 'uchar'
    )
  return (
    (elements.length === 2 && hasBaseElements()) ||
    (elements.length === 3 && hasBaseElements() && hasSHElements())
  )
}

function isFloatPly(elements: readonly PlyElement[]): boolean {
  return (
    elements.length === 1 &&
    elements[0].name === 'vertex' &&
    elements[0].properties.every((property) => property.type === 'float')
  )
}

async function readInto(stream: StreamBuf, target: ArrayBuffer, length: number): Promise<void> {
  const bytes = new Uint8Array(target)
  let cursor = 0
  while (cursor < length) {
    while (stream.remaining === 0) await stream.read()
    const toCopy = Math.min(length - cursor, stream.remaining)
    bytes.set(stream.data!.subarray(stream.head, stream.head + toCopy), cursor)
    cursor += toCopy
    stream.head += toCopy
  }
}

async function readCompressedPly(
  stream: StreamBuf,
  elements: readonly PlyElement[],
  comments: string[]
): Promise<GSplatCompressedData> {
  const numChunks = elements[0].count
  const chunkSize = elements[0].properties.length
  const numSplats = elements[1].count
  const chunkData = new Float32Array(numChunks * chunkSize)
  const vertexData = new Uint32Array(numSplats * 4)
  await readInto(stream, chunkData.buffer, chunkData.byteLength)
  await readInto(stream, vertexData.buffer, vertexData.byteLength)

  let shBands = 0
  let shData0: Uint8Array | null = null
  let shData1: Uint8Array | null = null
  let shData2: Uint8Array | null = null
  if (elements.length === 3) {
    shData0 = new Uint8Array(numSplats * 16)
    shData1 = new Uint8Array(numSplats * 16)
    shData2 = new Uint8Array(numSplats * 16)
    const sourceCoefficients = elements[2].properties.length / 3
    const chunkSplats = 1024
    const temporary = new Uint8Array(chunkSplats * sourceCoefficients * 3)
    for (let splat = 0; splat < numSplats; splat += chunkSplats) {
      const count = Math.min(chunkSplats, numSplats - splat)
      await readInto(stream, temporary.buffer, count * sourceCoefficients * 3)
      for (let local = 0; local < count; local++) {
        for (let coefficient = 0; coefficient < 15; coefficient++) {
          const target = (splat + local) * 16 + coefficient
          if (coefficient < sourceCoefficients) {
            shData0[target] = temporary[local * sourceCoefficients * 3 + coefficient]
            shData1[target] =
              temporary[local * sourceCoefficients * 3 + sourceCoefficients + coefficient]
            shData2[target] =
              temporary[local * sourceCoefficients * 3 + sourceCoefficients * 2 + coefficient]
          } else {
            shData0[target] = 127
            shData1[target] = 127
            shData2[target] = 127
          }
        }
      }
    }
    shBands = ({ 3: 1, 8: 2, 15: 3 } as const)[sourceCoefficients as 3 | 8 | 15]
  }

  return new GSplatCompressedData({
    numSplats,
    numChunks,
    chunkSize,
    chunkData,
    vertexData,
    hasColorRange: chunkSize === 18,
    shBands,
    comments,
    shData0,
    shData1,
    shData2,
  })
}

async function readFloatPly(
  stream: StreamBuf,
  elements: PlyElement[],
  comments: string[]
): Promise<GSplatData> {
  const element = elements[0]
  const properties = element.properties
  const storage = properties.map((property) => property.storage as Float32Array)
  const inputSize = properties.reduce((total, property) => total + property.byteSize!, 0)
  let vertex = 0
  let floatData: Float32Array | undefined
  const updateFloatData = () => {
    const buffer = stream.data!.buffer
    if (floatData?.buffer !== buffer) {
      floatData = new Float32Array(buffer, 0, buffer.byteLength / Float32Array.BYTES_PER_ELEMENT)
    }
  }
  updateFloatData()
  while (vertex < element.count) {
    while (stream.remaining < inputSize) {
      await stream.read()
      updateFloatData()
    }
    const count = Math.min(element.count - vertex, Math.floor(stream.remaining / inputSize))
    const floatOffset = stream.head / Float32Array.BYTES_PER_ELEMENT
    for (let property = 0; property < properties.length; property++) {
      for (let local = 0; local < count; local++) {
        storage[property][vertex + local] =
          floatData![floatOffset + local * properties.length + property]
      }
    }
    vertex += count
    stream.head += count * inputSize
  }
  return new GSplatData(elements, comments)
}

type PropertyReader = (stream: StreamBuf, index: number) => void

function createPropertyReader(property: PlyProperty): PropertyReader {
  if (!property.storage) return (stream) => (stream.head += property.byteSize!)
  switch (property.type) {
    case 'char':
      return (stream, index) => ((property.storage as Int8Array)[index] = stream.getInt8())
    case 'uchar':
      return (stream, index) => ((property.storage as Uint8Array)[index] = stream.getUint8())
    case 'short':
      return (stream, index) => ((property.storage as Int16Array)[index] = stream.getInt16())
    case 'ushort':
      return (stream, index) => ((property.storage as Uint16Array)[index] = stream.getUint16())
    case 'int':
      return (stream, index) => ((property.storage as Int32Array)[index] = stream.getInt32())
    case 'uint':
      return (stream, index) => ((property.storage as Uint32Array)[index] = stream.getUint32())
    case 'float':
      return (stream, index) => ((property.storage as Float32Array)[index] = stream.getFloat32())
    case 'double':
      return (stream, index) => ((property.storage as Float64Array)[index] = stream.getFloat64())
    default:
      throw new Error(`Unsupported property data type '${property.type}' in ply header`)
  }
}

async function readGeneralPly(
  stream: StreamBuf,
  elements: PlyElement[],
  comments: string[]
): Promise<GSplatData> {
  for (const element of elements) {
    const inputSize = element.properties.reduce((total, property) => total + property.byteSize!, 0)
    const readers = element.properties.map(createPropertyReader)
    let index = 0
    while (index < element.count) {
      while (stream.remaining < inputSize) await stream.read()
      const count = Math.min(element.count - index, Math.floor(stream.remaining / inputSize))
      for (let local = 0; local < count; local++) {
        for (const reader of readers) reader(stream, index)
        index++
      }
    }
  }
  return new GSplatData(elements, comments)
}

function findSequence(buffer: Uint8Array, search: Uint8Array): number {
  const last = buffer.length - search.length
  for (let index = 0; index <= last; index++) {
    let offset = 0
    for (; offset < search.length; offset++) {
      if (buffer[index + offset] !== search[offset]) break
    }
    if (offset === search.length) return index
  }
  return -1
}

function startsWith(buffer: Uint8Array, prefix: Uint8Array): boolean {
  if (buffer.length < prefix.length) return false
  for (let index = 0; index < prefix.length; index++) {
    if (buffer[index] !== prefix[index]) return false
  }
  return true
}

/** 冻结 PlayCanvas PLY stream reader 的 backend-neutral 直接移植。 */
export async function readPly(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  propertyFilter: ((name: string) => boolean) | null = null,
  progress: ((bytes: number) => void) | null = null,
  validateSplatCount: ((count: number) => void) | null = null
): Promise<PlyData> {
  const stream = new StreamBuf(reader, progress ?? undefined)
  let headerLength = -1
  while (headerLength === -1) {
    await stream.read()
    if (stream.tail >= MAGIC_BYTES.length && !startsWith(stream.data!, MAGIC_BYTES)) {
      throw new Error('Invalid ply header')
    }
    headerLength = findSequence(stream.data!, END_HEADER_BYTES)
  }
  const lines = new TextDecoder('ascii').decode(stream.data!.subarray(0, headerLength)).split('\n')
  const { elements, format, comments } = parseHeader(lines)
  if (format !== 'binary_little_endian') throw new Error('Unsupported ply format')
  const vertexCount = elements.find(({ name }) => name === 'vertex')?.count
  if (vertexCount === undefined) throw new Error('PLY header 缺少 vertex element')
  validateSplatCount?.(vertexCount)
  stream.head = headerLength + END_HEADER_BYTES.length
  stream.compact()

  if (isCompressedPly(elements)) return readCompressedPly(stream, elements, comments)
  for (const element of elements) {
    for (const property of element.properties) {
      const storageType = DATA_TYPE_MAP.get(property.type)!
      property.storage =
        !propertyFilter || propertyFilter(property.name)
          ? (new storageType(element.count) as PlyStorage)
          : null
    }
  }
  return isFloatPly(elements) && elements[0].properties.every((property) => property.storage)
    ? readFloatPly(stream, elements, comments)
    : readGeneralPly(stream, elements, comments)
}

/** PLY parser 只负责 fetch/data/resource 构造；backend 差异全部留在 adapter leaf。 */
export class PlyParser implements IGSplatParser<PlyResource, PlyParserOptions> {
  handler?: GSplatHandler<PlyResource, PlyParserOptions> | null

  canParse(context: GSplatParserContext<PlyParserOptions>): boolean {
    return context.ext === 'ply'
  }

  async load(
    url: GSplatLoadUrlObject,
    context: GSplatParserContext<PlyParserOptions>
  ): Promise<PlyResource> {
    const options = context.options
    if (!options?.adapter) throw new Error('PlyParser: 缺少 GSplatGraphicsAdapter')
    let reader: ReadableStreamDefaultReader<Uint8Array>
    let total: number
    if (url.load instanceof Blob) {
      const buffer = await url.load.arrayBuffer()
      const bytes = new Uint8Array(buffer)
      reader = new ReadableStream<Uint8Array>({
        start(controller) {
          controller.enqueue(bytes)
          controller.close()
        },
      }).getReader()
      total = bytes.byteLength
    } else {
      const response = await fetch(
        url.load,
        context.signal ? { signal: context.signal } : undefined
      )
      if (!response.ok || !response.body) {
        throw new Error(`PlyParser: 加载失败 ${response.status} ${response.statusText}`)
      }
      reader = response.body.getReader()
      total = Number.parseInt(response.headers.get('content-length') ?? '0', 10)
    }
    let loaded = 0
    const data = await readPly(
      reader,
      options.propertyFilter ?? null,
      (bytes) => {
        loaded += bytes
        options.progress?.(loaded, total)
      },
      options.validateSplatCount ?? null
    )
    if (context.signal?.aborted) throw new DOMException('PlyParser load 已取消', 'AbortError')
    options.onData?.(data)

    if (data instanceof GSplatCompressedData && !options.decompress) {
      return GSplatCompressedResource.create(options.adapter, data, {
        prepareCenters: options.prepareCenters,
      })
    }
    const uncompressed = data instanceof GSplatCompressedData ? data.decompress() : data
    // 与冻结上游一致：只有原生 uncompressed PLY 在 Resource 创建前 reorder；
    // compressed 数据即便请求 decompress 也保持原 packed index 顺序。
    if (data instanceof GSplatData && (options.reorder ?? true)) uncompressed.reorderData()
    return GSplatResource.create(options.adapter, uncompressed, {
      prepareCenters: options.prepareCenters,
    })
  }
}
